/**
 * Color scale for the past-mode ambulance-transport RISK score.
 *
 * Risk score = weekly ambulance transports per 100,000 population
 * (人口10万対・週). Using a population denominator lets us compare a small
 * prefecture against a large one fairly — a key point for the sales pitch,
 * since a high score on a NOT-especially-hot week is the evidence we want
 * to surface.
 *
 * Bands mirror the 環境省 WBGT severity language (青→黄→橙→赤→暗赤) so the
 * legend reads the same way as the forecast tab's WBGT scale. Thresholds
 * derived from the 2021-2025 distribution across all 47 prefectures:
 *   p50≈2.0, p75≈5.3, p90≈8.7, p95≈11.0, p99≈15.7, max≈27.9.
 */

export type RiskBand = {
  min: number;
  max: number;
  color: string;
  label: string;
};

export const RISK_BANDS: readonly RiskBand[] = [
  { min: 25,        max: Infinity, color: '#4a0014', label: '極めて高い' },
  { min: 15,        max: 25,       color: '#7e0023', label: '非常に高い' },
  { min: 10,        max: 15,       color: '#e60012', label: '警戒' },
  { min: 6,         max: 10,       color: '#ef8200', label: '高い' },
  { min: 3,         max: 6,        color: '#f3c700', label: 'やや高い' },
  { min: 1,         max: 3,        color: '#54b59a', label: '低い' },
  { min: -Infinity, max: 1,        color: '#1d8eff', label: 'ごく低い' },
] as const;

export const NO_DATA_COLOR = '#e2e4ea';

export function colorForRisk(v: number | null | undefined): string {
  if (v == null || Number.isNaN(v)) return NO_DATA_COLOR;
  for (const b of RISK_BANDS) {
    if (v >= b.min && v < b.max) return b.color;
  }
  return RISK_BANDS[RISK_BANDS.length - 1]!.color;
}

export function riskLabel(v: number | null | undefined): string {
  if (v == null || Number.isNaN(v)) return 'データなし';
  for (const b of RISK_BANDS) {
    if (v >= b.min && v < b.max) return b.label;
  }
  return RISK_BANDS[0]!.label;
}
